(ns io.daskul.webapp.widgets.child-aggregates
  "Child aggregate UI components for separate table relationships.

   Unlike embedded relationships (stored in JSONB), child aggregates are
   separate database tables with FK references to the parent.

   Uses slide-over (Drawer) for add/edit forms and FSM transitions.
   Table displays data with action icons that open the slide-over."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.form.field :as ff]
   [io.daskul.webapp.widgets.layout.slide-over :as slide-over]
   [io.daskul.webapp.widgets.fsm.transitions :as fsm.transitions]
   [io.daskul.webapp.widgets.fsm.transition-logic :as transition-logic]
   [io.daskul.webapp.widgets.display :as display]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- format-field-label
  "Convert field name to human-readable label.
   :suggested-frequency -> \"Suggested Frequency\""
  [field-name]
  (->> (-> (name field-name)
           (str/split #"-"))
       (map str/capitalize)
       (str/join " ")))

(defn- format-value
  "Format a value for display in the table."
  [value _field-name]
  (if (nil? value) "-" (str value)))

(defn- get-column-value
  "Extract column value from item using column config.
   Supports :path for both simple ([:a :b]) and compound ([[:a :b] \" \" [:c :d]]) paths."
  [item column]
  (if-let [path (:path column)]
    (display/resolve-path item path)
    (get item (:key column))))

(defn- resolve-defaults
  "Resolve defaults map against a parent record.
   Paths follow [:source :key ...] convention — currently supports :parent source.
   Example: {:amount [:parent :balance]} with parent {:balance 500} → {:amount 500}"
  [defaults parent-record]
  (when (and (seq defaults) parent-record)
    (->> defaults
         (keep (fn [[field-key path]]
                 (when (and (vector? path) (= :parent (first path)))
                   (let [value (get-in parent-record (vec (rest path)))]
                     (when (some? value)
                       [field-key value])))))
         (into {})
         not-empty)))

(defn- redundant-for-parent?
  "Returns true if a list column is redundant for the given parent aggregate.
   A column is redundant when all its :path segments reference the parent agg key,
   meaning it would just repeat what's already visible on the parent detail page.
   E.g., a 'Guardian' column with :path [[:guardian :person :first-name] ...]
   is redundant when parent-agg-key is :guardian."
  [column parent-agg-key]
  (when-let [path (:path column)]
    (let [segments (filterv vector? path)]
      (and (seq segments)
           (every? #(= (first %) parent-agg-key) segments)))))

;; ============================================================================
;; Default Form Field (simple wrapper around lib form-field)
;; ============================================================================

(defui default-form-field
  "Default form-field for slide-over forms. Wraps lib's form-field.
   Products can override with their own connected form-field."
  [{:keys [agg-key field value on-change disabled form-data
           external-error on-clear-error]}]
  ($ ff/form-field
    {:agg-key agg-key
     :field field
     :value value
     :on-change on-change
     :disabled disabled
     :form-data form-data
     :external-error external-error
     :on-clear-error on-clear-error}))

;; ============================================================================
;; Standalone Form (used by custom-add-components like billables-add)
;; ============================================================================

(defui child-inline-form
  "Standalone form for adding/editing child items.
   Used by custom-add-components that need a standard form inside slide-overs.

   FK field is auto-injected (not shown). State field excluded."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field
           schema mode initial-data child-id on-close
           form-field-component]}]
  (let [fields (:fields schema)
        saving? (urf/use-subscribe [::agg.subs/child-saving? parent-agg-key parent-id child-agg-key])
        form-error (urf/use-subscribe [::agg.subs/child-form-error parent-agg-key parent-id child-agg-key])
        defaults (when (and (= mode :add) (nil? initial-data))
                   (->> fields
                        (keep (fn [field]
                                (when (some? (:default field))
                                  [(:name field) (:default field)])))
                        (into {})))
        [form-data set-form-data!] (uix/use-state (or initial-data defaults {}))
        ;; Drop the FK, the FSM state, and any :readonly field. Readonly marks
        ;; :field/protected fields the server excludes from the patch schema
        ;; (and rejects as "disallowed key" — #831); they must not be submitted.
        editable-fields (->> fields
                             (remove #(or (#{fk-field :state} (:name %))
                                          (:readonly %))))
        valid-field-keys (set (map :name editable-fields))
        render-field (or form-field-component default-form-field)

        handle-submit
        (fn [e]
          (.preventDefault e)
          (.stopPropagation e)
          (let [clean-data (select-keys form-data valid-field-keys)]
            (if (= mode :edit)
              (rf/dispatch [::agg.events/patch-child parent-agg-key parent-id child-agg-key fk-field child-id clean-data])
              (rf/dispatch [::agg.events/create-child parent-agg-key parent-id child-agg-key fk-field clean-data]))))]

    (let [error-msg (ff/form-level-error-message form-error editable-fields)]
      ($ ui/box {:p "md"
                 :style {:backgroundColor "var(--mantine-color-gray-0)"
                         :borderRadius "var(--mantine-radius-sm)"}}
        ($ :form {:onSubmit handle-submit}
          ($ ui/stack {:gap "sm"}
            (when error-msg
              ($ ui/error-alert {:mb "xs"} error-msg))
            (for [field editable-fields]
              (let [field-key (:name field)
                    raw-err (get-in form-error [:field-errors field-key])]
                ($ render-field
                  {:key (name field-key)
                   :agg-key child-agg-key
                   :field field
                   :value (get form-data field-key)
                   :on-change #(set-form-data! (assoc form-data field-key %))
                   ;; See lookup-create: a widget owning two columns needs the
                   ;; form-level setter, not just its own key.
                   :set-form-field! #(set-form-data! (assoc form-data %1 %2))
                   :form-data form-data
                   :external-error (ff/humanize-error raw-err)
                   :on-clear-error #(rf/dispatch [::agg.events/clear-child-field-error
                                                   parent-agg-key parent-id child-agg-key field-key])})))

          ;; Action buttons
          ($ ui/group {:justify "flex-end" :gap "sm"}
            ($ ui/button {:variant "subtle" :color "gray" :size "sm" :onClick on-close}
              "Cancel")
            ($ ui/button {:type "submit" :size "sm" :loading saving?}
              (if (= mode :edit) "Save" "Add")))))))))

;; ============================================================================
;; Slide-over Form (add/edit with FSM transitions)
;; ============================================================================

(defui child-slide-over-form
  "Form inside slide-over for adding/editing child items.
   Edit mode includes FSM state badge and transition buttons at top.

   FK field is auto-injected (not shown). State field excluded.
   Form data is local state; FSM data comes from live item (subscription)."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field
           schema mode item on-close on-delete form-field-component
           transitioning? transition-event deleting? delete-ui]}]
  (let [fields (:fields schema)
        saving? (urf/use-subscribe [::agg.subs/child-saving? parent-agg-key parent-id child-agg-key])
        [confirm-delete? set-confirm-delete!] (uix/use-state false)
        form-error (urf/use-subscribe [::agg.subs/child-form-error parent-agg-key parent-id child-agg-key])

        ;; Initialize form data from item (edit) or empty (add)
        [form-data set-form-data!] (uix/use-state
                                    (if (= mode :edit)
                                      (select-keys item (map :name fields))
                                      {}))

        ;; FSM data from live item (updates after transitions)
        fsm-data (when (= mode :edit) (get-in item [:_meta :fsm]))
        state-ui (:state fsm-data)
        ;; SMS-122: same source-level filter as use-fsm — drop hidden, primary
        ;; by :variant, humanize unlabeled — so child FSM panels honor :ui too.
        transitions (transition-logic/prepare-transitions (:transitions fsm-data))

        ;; Get editable fields (exclude FK, state, and readonly). Readonly marks
        ;; :field/protected fields the server excludes from the patch schema (and
        ;; rejects as "disallowed key" — #831); they must not be submitted.
        editable-fields (->> fields
                             (remove #(or (#{fk-field :state} (:name %))
                                          (:readonly %))))
        valid-field-keys (set (map :name editable-fields))

        render-field (or form-field-component default-form-field)

        handle-submit
        (fn [e]
          (.preventDefault e)
          (.stopPropagation e)
          (let [clean-data (select-keys form-data valid-field-keys)]
            (if (= mode :edit)
              (rf/dispatch [::agg.events/patch-child parent-agg-key parent-id
                            child-agg-key fk-field (:id item) clean-data])
              (rf/dispatch [::agg.events/create-child parent-agg-key parent-id
                            child-agg-key fk-field clean-data]))))

        handle-transition
        (fn [event params]
          (rf/dispatch [::agg.events/transition-child parent-agg-key parent-id
                        child-agg-key fk-field (:id item) event params]))]

    (let [error-msg (ff/form-level-error-message form-error editable-fields)]
      ($ ui/stack {:gap "md"}
        ;; FSM section (edit mode only)
        (when (and (= mode :edit) (or state-ui (seq transitions)))
          ($ :<>
            ($ ui/paper {:p "md" :withBorder true}
              ($ ui/stack {:gap "sm"}
                (when state-ui
                  ($ ui/group {:justify "space-between"}
                    ($ ui/text {:fw 500 :size "sm" :c "dimmed"} "Status")
                    (display/render-display-value
                     (:state item)
                     (display/state-ui->display-config (:state item) state-ui))))
                (when (seq transitions)
                  ($ fsm.transitions/transition-action-panel
                    {:transitions transitions
                     :on-transition handle-transition
                     :loading-event transition-event
                     :transitioning? transitioning?
                     :agg-key child-agg-key
                     :record item}))))
            ($ ui/divider {:my "xs" :label "Details" :labelPosition "center"})))

        ;; Form
        ($ :form {:onSubmit handle-submit}
          ($ ui/stack {:gap "sm"}
            (when error-msg
              ($ ui/error-alert {:mb "xs"} error-msg))

            ;; Form fields
            (for [field editable-fields]
              (let [field-key (:name field)
                    raw-err (get-in form-error [:field-errors field-key])]
                ($ render-field
                  {:key (name field-key)
                   :agg-key child-agg-key
                   :field field
                   :value (get form-data field-key)
                   :on-change #(set-form-data! (assoc form-data field-key %))
                   ;; See lookup-create: a widget owning two columns needs the
                   ;; form-level setter, not just its own key.
                   :set-form-field! #(set-form-data! (assoc form-data %1 %2))
                   :form-data form-data
                   :external-error (ff/humanize-error raw-err)
                   :on-clear-error #(rf/dispatch [::agg.events/clear-child-field-error
                                                   parent-agg-key parent-id child-agg-key field-key])})))

            ;; Action buttons — [Delete] on left, [Cancel][Save] on right
            ($ ui/group {:justify "space-between"}
              (if (and (= mode :edit) on-delete)
                ($ ui/button {:variant "subtle" :color "red" :size "sm"
                              :onClick #(set-confirm-delete! true)}
                  (:label delete-ui))
                ($ :div))
              ($ ui/group {:gap "sm"}
                ($ ui/button {:variant "subtle" :color "gray" :size "sm" :onClick on-close}
                  "Cancel")
                ($ ui/button {:type "submit" :size "sm" :loading saving?}
                  (if (= mode :edit) "Save" "Add"))))

            ;; Delete confirmation modal
            ;; An aggregate may name this action itself. "Delete … cannot be
            ;; undone" is simply false for a junction row, where both records
            ;; survive and the link can be made again.
            ($ ui/modal {:opened confirm-delete?
                         :onClose #(set-confirm-delete! false)
                         :title (get-in delete-ui [:confirm :title])
                         :centered true
                         :size "sm"}
              ($ ui/stack {:gap "md"}
                ($ ui/text {:size "sm"}
                   (get-in delete-ui [:confirm :text]))
                ($ ui/group {:justify "flex-end" :gap "sm"}
                  ($ ui/button {:variant "subtle" :color "gray" :size "sm"
                                :onClick #(set-confirm-delete! false)}
                    "Cancel")
                  ($ ui/destructive-button {:size "sm" :loading deleting?
                                            :onClick on-delete}
                    (get-in delete-ui [:confirm :label])))))))))))

;; ============================================================================
;; Child Aggregate List Component
;; ============================================================================

(defn- humanize-roles
  "\"school-admin\" -> \"a school admin\"; several read as a list."
  [roles]
  (let [phrases (map #(str "a " (str/replace (name %) "-" " ")) roles)]
    (case (count phrases)
      0 "someone else"
      1 (first phrases)
      (str (str/join ", " (butlast phrases)) " or " (last phrases)))))

(defui ^:private gated-action
  "A row action that is disabled — with a reason — when the caller's role may not
   use it, instead of silently missing. The API has always refused these; the
   button just did not say so, and a 403 on click is a worse answer than a
   disabled control that explains itself.

   The verb is the one the aggregate declares, not a hardcoded \"delete\": on a
   junction row the button says Unlink, and a tooltip that says \"delete\" puts
   back exactly the ambiguity the label was changed to remove."
  [{:keys [denied verb label color on-click children]}]
  (if denied
    ($ ui/tooltip {:label (let [singular (if (and label (str/ends-with? label "s"))
                                           (subs label 0 (dec (count label)))
                                           (or label "record"))
                                ;; "unlink a linked guardian" is redundant —
                                ;; when the verb already names the relationship,
                                ;; the object is the far side of it.
                                singular (if (and (not= verb "delete")
                                                  (str/starts-with? (str/lower-case singular) "linked "))
                                           (subs singular (count "Linked "))
                                           singular)]
                            (str "Only " (humanize-roles (:roles denied))
                                 " can " verb " a " (str/lower-case singular) "."))
                   :withArrow true
                   :multiline true
                   :w 240}
      ;; A disabled button reports no pointer events, so the tooltip needs a
      ;; wrapper that does — otherwise the explanation never appears.
      ($ :span {:style {:display "inline-flex" :cursor "not-allowed"}}
        ($ ui/table-action-icon {:color color :disabled true} children)))
    ($ ui/table-action-icon {:color color :onClick on-click} children)))

(defui ^:private confirm-popover
  "Confirmation anchored to the control that raised it.

   For an action that DETACHES rather than destroys. It keeps the row on screen —
   which a modal manages only by accident and the old inline strip destroyed
   outright, replacing the very row you were being asked about. Dismisses on
   click-away, which is right when the record survives and wrong when it does
   not: `:confirm {:widget :popover}` opts in, and an aggregate that says
   nothing gets the modal."
  [{:keys [opened? on-close on-open on-confirm confirm-text label loading? color children]}]
  ($ ui/popover {:opened opened? :onClose on-close :withArrow true
                 :position "left" :shadow "md" :width 300
                 :trapFocus true :withinPortal true}
    ;; Popover.Target clones its child to attach a ref, so the child has to
    ;; FORWARD one. Mantine's ActionIcon does; every defui in this codebase does
    ;; not — including ui/table-action-icon, which merely wraps it. Hand the
    ;; target a defui and the dropdown anchors to nothing and lands in the
    ;; viewport corner, silently. So this reaches past the wrapper to the raw
    ;; component and re-applies the props the wrapper would have added.
    ($ ui/popover-target
      ($ ui/action-icon {:variant "subtle" :size "md" :color color :onClick on-open}
         children))
    ($ ui/popover-dropdown
      ($ ui/stack {:gap "sm"}
        ($ ui/text {:size "sm"} confirm-text)
        ($ ui/group {:justify "flex-end" :gap "xs"}
          ($ ui/button {:variant "subtle" :color "gray" :size "xs" :onClick on-close}
             "Cancel")
          ($ ui/destructive-button {:size "xs" :loading loading? :onClick on-confirm}
             label))))))

(defui child-aggregate-list
  "Table component for child aggregates (separate table with FK).
   Uses slide-over (Drawer) for add/edit forms and FSM transitions.

   Props:
   - parent-agg-key: keyword - parent aggregate key (e.g., :program)
   - parent-id: string - parent record ID
   - child-agg-key: keyword - child aggregate key (e.g., :package)
   - fk-field: keyword - FK field name (e.g., :program-id)
   - title: string - section title (e.g., \"Packages\")
   - columns: vector - optional columns to display, defaults to schema fields
   - list-columns: vector - optional list columns config (from aggregate UI config)
   - after-read-fields: map - optional {field-name {:display {:type :badge :values {...}}}}
   - form-field-component: component - optional custom form field renderer
   - read-only?: boolean - when true, hides add/edit/delete buttons
   - child-restrictions: map - FSM state-based restrictions (e.g., {:create false})
   - custom-add-component: component - optional, replaces standard form in add slide-over
   - parent-record: map - optional, passed to custom-add-component for parent context
   - view-href-fn: (fn [item] href-string) - when provided, renders a view icon that links to the detail page
   - exclude-ids: set - child ids to omit from the rows. For a page that already
     presents one of them in a dedicated section (#974's primary payer): the row
     would be a duplicate, but the record still belongs to this list, so it is
     hidden here rather than filtered out of the underlying data.
   - initial-edit-id: string - opens the edit slide-over for that child. Changing
     it re-opens, so a control outside this list (an Edit button in that
     dedicated section) can drive the same form."
  [{:keys [parent-agg-key parent-id child-agg-key fk-field title
           columns list-columns after-read-fields form-field-component read-only?
           child-restrictions custom-add-component parent-record view-href-fn
           column-link-fns defaults refresh-parent junction empty-message add
           initial-edit-id on-slide-over-change exclude-ids denied-ops delete-ui]}]
  (let [all-items (urf/use-subscribe [::agg.subs/child-items parent-agg-key parent-id child-agg-key])
        items (if (seq exclude-ids)
                (vec (remove #(contains? exclude-ids (str (:id %))) all-items))
                all-items)
        loading? (urf/use-subscribe [::agg.subs/child-loading? parent-agg-key parent-id child-agg-key])
        save-success? (urf/use-subscribe [::agg.subs/child-save-success? parent-agg-key parent-id child-agg-key])
        deleting? (urf/use-subscribe [::agg.subs/child-deleting? parent-agg-key parent-id child-agg-key])
        transitioning? (urf/use-subscribe [::agg.subs/child-transitioning? parent-agg-key parent-id child-agg-key])
        transition-event (urf/use-subscribe [::agg.subs/child-transition-event parent-agg-key parent-id child-agg-key])
        error (urf/use-subscribe [::agg.subs/child-error parent-agg-key parent-id child-agg-key])
        schema (urf/use-subscribe [::agg.subs/schema child-agg-key :create])
        ;; Edit is a PATCH: use the patch schema, which marks :field/protected
        ;; fields :readonly so they are neither rendered nor submitted (#831).
        ;; Add keeps the create schema (protected FKs are set on create).
        edit-schema (urf/use-subscribe [::agg.subs/schema child-agg-key :patch])
        fields (:fields schema)

        ;; Column config
        list-columns (when list-columns
                       (filterv #(not (redundant-for-parent? % parent-agg-key)) list-columns))
        use-columns? (seq list-columns)
        display-fields (if columns
                         (filter #(contains? (set columns) (:name %)) fields)
                         (filter #(not= fk-field (:name %)) fields))

        create-disabled? (if (true? (:create child-restrictions))
                           false
                           (or read-only? (false? (:create child-restrictions))))

        ;; Column count for colSpan on delete confirmation rows
        has-fsm-col? (and (not use-columns?) (some #(get-in % [:_meta :fsm :state]) items))
        has-actions-col? (or (not read-only?) view-href-fn)
        total-cols (+ (if use-columns? (count list-columns) (count display-fields))
                      (if has-fsm-col? 1 0)
                      (if has-actions-col? 1 0))

        ;; Slide-over state: nil | {:mode :add} | {:mode :edit :id "..."}
        [slide-over-state set-slide-over-raw!] (uix/use-state
                                                 (when initial-edit-id
                                                   {:mode :edit :id initial-edit-id}))
        set-slide-over! (uix/use-callback
                         (fn [state]
                           (set-slide-over-raw! state)
                           (when on-slide-over-change
                             (on-slide-over-change (when (= (:mode state) :edit) (:id state)))))
                         [on-slide-over-change])
        ;; The initial value above only applies on mount. Syncing here is what
        ;; lets an outside control open this form on an already-mounted list —
        ;; and it is the same path a ?edit= deep link takes.
        _ (uix/use-effect
           (fn []
             (when initial-edit-id
               (set-slide-over-raw! {:mode :edit :id initial-edit-id}))
             js/undefined)
           [initial-edit-id])
        ;; Current item for slide-over edit (from live subscription — always fresh)
        current-item (when (= (:mode slide-over-state) :edit)
                       ;; all-items, not the filtered rows: a row hidden by
                       ;; :exclude-ids is still editable from wherever the page
                       ;; shows it instead, and its form has to find it.
                       (some #(when (= (:id %) (:id slide-over-state)) %) all-items))

        ;; Inline delete confirmation: nil or item-id
        [delete-confirm-id set-delete-confirm-id!] (uix/use-state nil)

        ;; Resolve defaults from parent record for add mode
        resolved-defaults (resolve-defaults defaults parent-record)

        clear-form-state!
        (fn []
          (rf/dispatch [::agg.events/clear-child-save-success parent-agg-key parent-id child-agg-key])
          (rf/dispatch [::agg.events/clear-child-form-error parent-agg-key parent-id child-agg-key]))

        handle-add
        (fn []
          (clear-form-state!)
          (set-slide-over! {:mode :add}))

        handle-edit
        (fn [item]
          (clear-form-state!)
          (set-slide-over! {:mode :edit :id (:id item)}))

        handle-close
        (fn []
          (clear-form-state!)
          (set-slide-over! nil))

        handle-delete-click
        (fn [item-id]
          ;; Close slide-over if editing the item being deleted
          (when (and (= (:mode slide-over-state) :edit) (= (:id slide-over-state) item-id))
            (set-slide-over! nil))
          (set-delete-confirm-id! item-id))

        handle-delete-confirm
        (fn []
          (rf/dispatch [::agg.events/delete-child parent-agg-key parent-id child-agg-key fk-field delete-confirm-id])
          (set-delete-confirm-id! nil))

        handle-delete-cancel
        (fn [] (set-delete-confirm-id! nil))

        handle-slide-over-delete
        (fn []
          (when-let [id (:id slide-over-state)]
            (rf/dispatch [::agg.events/delete-child parent-agg-key parent-id child-agg-key fk-field id])))

        ;; Slide-over form props
        slide-over-form-props
        {:parent-agg-key parent-agg-key
         :parent-id parent-id
         :child-agg-key child-agg-key
         :fk-field fk-field
         :schema schema
         :on-close handle-close
         :on-delete handle-slide-over-delete
         :deleting? deleting?
         :form-field-component form-field-component
         :transitioning? transitioning?
         :transition-event transition-event
         :delete-ui delete-ui}]

    ;; Close slide-over on save success + refresh parent
    (uix/use-effect
     (fn []
       (when save-success?
         (set-slide-over! nil)
         (rf/dispatch [::agg.events/clear-child-save-success parent-agg-key parent-id child-agg-key])
         (when refresh-parent
           (rf/dispatch [::agg.events/get parent-agg-key parent-id])))
       js/undefined)
     [set-slide-over! save-success? parent-agg-key parent-id child-agg-key refresh-parent])

    ;; Refresh parent after child transition or delete completes
    (let [prev-transitioning (uix/use-ref false)
          prev-deleting (uix/use-ref false)]
      (uix/use-effect
       (fn []
         (when refresh-parent
           (when (and @prev-transitioning (not transitioning?))
             (rf/dispatch [::agg.events/get parent-agg-key parent-id]))
           (when (and @prev-deleting (not deleting?))
             (rf/dispatch [::agg.events/get parent-agg-key parent-id])))
         (reset! prev-transitioning transitioning?)
         (reset! prev-deleting deleting?)
         js/undefined)
       [transitioning? deleting? refresh-parent parent-agg-key parent-id]))

    ;; Close slide-over if item was deleted (no longer in list)
    (uix/use-effect
     (fn []
       (when (and (= (:mode slide-over-state) :edit) (nil? current-item))
         (set-slide-over! nil))
       js/undefined)
     [set-slide-over! slide-over-state current-item])

    ;; Clear form error on unmount
    (uix/use-effect
     (fn []
       (fn []
         (rf/dispatch [::agg.events/clear-child-form-error parent-agg-key parent-id child-agg-key])))
     [parent-agg-key parent-id child-agg-key])

    ;; Fetch children and schema on mount
    (uix/use-effect
     (fn []
       (when parent-id
         (rf/dispatch [::agg.events/list-children parent-agg-key parent-id child-agg-key fk-field])
         (rf/dispatch [::agg.events/fetch-schema child-agg-key :create])
         (rf/dispatch [::agg.events/fetch-schema child-agg-key :patch]))
       js/undefined)
     [parent-agg-key parent-id child-agg-key fk-field])

    (let [content
          (cond
            loading?
            ($ ui/centered-loader {:py "md" :size "sm"})

            error
            ($ ui/error-alert "Failed to load items")

            (empty? items)
            ;; DASHED AND GREY, and still visible (F-060). Empty is
            ;; information in a B2B tool — "this guardian has no invoices" is
            ;; often exactly what someone came to check — so the relation
            ;; keeps its place and says so, rather than collapsing to nothing
            ;; and leaving you unsure whether it loaded.
            ;;
            ;; Dashed rather than solid because the border is describing an
            ;; absence: a solid card reads as a thing that is there.
            ($ ui/box {:style {:border "1px dashed var(--border)"
                               :borderRadius "var(--mantine-radius-md)"
                               :padding "var(--section-pad, 20px)"
                               :textAlign "center"
                               :background "var(--surface-subtle)"}}
              ;; The declared reason when there is one. "No lead has named
              ;; this guardian" states a fact; the generic fallback, "No leads
              ;; defined yet", implies you were supposed to define one — wrong
              ;; for a relation you never create from this page.
              ($ ui/text {:c "dimmed" :size "sm"}
                (or empty-message
                    (str "No " (str/lower-case (or title "items")) " defined yet."))))

            :else
            ($ ui/scroll-area {:type "auto"}
            ($ ui/table {:striped true :withTableBorder true :withColumnBorders true}
              ($ ui/thead
                ;; TWO-BAND HEADER (F-060, #703). A junction row is two
                ;; different things side by side: the far record, which is
                ;; read-only here because changing it means opening that
                ;; record, and the link's own fields, which are editable
                ;; inline. Without the bands an admin cannot tell that
                ;; editing `Relationship` is safe while editing `Student`
                ;; would mean something else entirely.
                ;;
                ;; The split is derived server-side from the junction's own
                ;; schema, so a column belongs to the link band purely by
                ;; being one of its fields — no per-table column lists.
                (when-let [{:keys [far link]} junction]
                  (let [link-keys (into #{} (map (comp keyword :field)) link)
                        rendered (if use-columns?
                                   (mapv #(keyword (name (:key %))) list-columns)
                                   (mapv #(keyword (name (:name %))) display-fields))
                        link-n (count (filterv link-keys rendered))
                        far-n (- (count rendered) link-n)
                        extra (+ (if has-fsm-col? 1 0) (if has-actions-col? 1 0))
                        band {:fontSize "var(--eyebrow-size)"
                              :fontWeight "var(--eyebrow-weight)"
                              :letterSpacing "var(--eyebrow-tracking)"
                              :textTransform "uppercase"
                              :color "var(--text-faint)"
                              :fontFamily "var(--font-heading)"}]
                    (when (and (pos? far-n) (pos? link-n))
                      ($ ui/tr
                        ($ ui/th {:colSpan far-n :style band}
                          (str "The " (some-> far :aggregate name
                                              (str/replace "-" " "))))
                        ($ ui/th {:colSpan (+ link-n extra) :style band}
                          "On the link")))))
                ($ ui/tr
                  (if use-columns?
                    (for [col list-columns]
                      (let [k (:key col)]
                        ($ ui/th {:key (if (keyword? k) (name k) (str k))} (:label col))))
                    (for [field display-fields]
                      (let [field-label (or (get-in field [:ui :label]) (format-field-label (:name field)))]
                        ($ ui/th {:key (name (:name field))} field-label))))
                  (when has-fsm-col?
                    ($ ui/th {:key "state"} "State"))
                  (when has-actions-col?
                    ($ ui/th {:key "actions"} ""))))
              ($ ui/tbody
                (for [item items]
                  ($ :<> {:key (:id item)}
                    ;; The row is NEVER replaced by its own confirmation. It used to
                    ;; be: a strip took the row's place, so at the moment you were
                    ;; asked "remove this one?" the one in question left the screen.
                    ;; The confirmation is anchored to the control instead — see
                    ;; confirm-popover in the actions cell.
                    (let [_ nil]
                      ;; Normal data row. Not whole-row-clickable — edit is via the
                      ;; pencil action in the actions column (consistent with the main
                      ;; list view), and inline cell links soft-nav via reitit. SMS-114.
                      ($ ui/tr {}
                        (if use-columns?
                          (for [col list-columns]
                            (let [col-key (:key col)
                                  display-config (get-in after-read-fields [col-key :display])
                                  link-fn (get column-link-fns col-key)
                                  link-url (when link-fn (link-fn item))
                                  cell-content
                                  (cond
                                    ;; FSM state — a dense child table, so the
                                    ;; lighter :indicator dot by default (#858);
                                    ;; a column :display can override the weight.
                                    (and (= col-key :state) (get-in item [:_meta :fsm :state]))
                                    (display/render-display-value
                                     (:state item)
                                     (display/state-cell-display-config
                                      (:state item) (get-in item [:_meta :fsm :state]) (:display col)))

                                    ;; Display renderer (badge etc.) from after-read-fields
                                    display-config
                                    (display/render-display-value (get-column-value item col) display-config)

                                    ;; Column-level display (for regular fields)
                                    (:display col)
                                    (display/render-display-value (get-column-value item col) (:display col))

                                    ;; Default text
                                    :else
                                    (let [v (get-column-value item col)]
                                      (if (nil? v) "-" (str v))))]
                              ($ ui/td {:key (if (keyword? col-key) (name col-key) (str col-key))}
                                (if link-url
                                  (let [text (or (get-in col [:link-to :link-text]) cell-content)]
                                    ($ ui/anchor {:href link-url :size "sm" :c "inherit" :underline "hover"}
                                      text))
                                  cell-content))))
                          (for [field display-fields]
                            (let [field-key (:name field)
                                  value (get item field-key)]
                              ($ ui/td {:key (name field-key)}
                                (format-value value field-key)))))
                        ;; State column (only if not using list columns) — a dense
                        ;; child table, so the lighter :indicator dot (#858).
                        (when has-fsm-col?
                          (let [fsm-state (get-in item [:_meta :fsm :state])]
                            ($ ui/td {:key "state"}
                              (when fsm-state
                                (display/render-display-value
                                 (:state item)
                                 (display/state-cell-display-config (:state item) fsm-state nil))))))
                        ;; Actions column
                        (when has-actions-col?
                          ($ ui/td {:key "actions"}
                            ($ ui/group {:gap "xs" :justify "center"}
                              (when view-href-fn
                                ($ :a {:href (view-href-fn item)
                                       :style {:display "flex" :alignItems "center"}}
                                  ($ ui/table-action-icon
                                    {:color "gray"
                                     :component "span"}
                                    ($ icons/icon-external-link {:size 18}))))
                              (when-not read-only?
                                ($ :<>
                                  ($ gated-action
                                    {:denied (:patch denied-ops)
                                     :verb "edit"
                                     :label (or title (name child-agg-key))
                                     :color "gray"
                                     :on-click (fn [e]
                                                 (.stopPropagation e)
                                                 (handle-edit item))}
                                    ($ icons/icon-edit {:size 18}))
                                  ;; A bin means the record stops existing. On a
                                  ;; junction row it does not — and the config
                                  ;; already said which this is.
                                  (let [glyph (if (= "unlink" (:icon delete-ui))
                                                ($ icons/icon-unlink {:size 18})
                                                ($ icons/icon-trash {:size 18}))
                                        denied (:delete denied-ops)]
                                    (cond
                                      ;; Denied: the gated button explains itself and
                                      ;; opens nothing.
                                      denied
                                      ($ gated-action
                                        {:denied denied
                                         :verb (str/lower-case (:label delete-ui))
                                         :label (or title (name child-agg-key))
                                         :color "red"}
                                        glyph)

                                      ;; Anchored to the row, so it stays put
                                      ;; while you answer.
                                      (= "popover" (get-in delete-ui [:confirm :widget]))
                                      ($ confirm-popover
                                        {:opened? (= delete-confirm-id (:id item))
                                         :on-close handle-delete-cancel
                                         :on-open #(handle-delete-click (:id item))
                                         :on-confirm handle-delete-confirm
                                         :loading? deleting?
                                         :color "red"
                                         :label (get-in delete-ui [:confirm :label])
                                         :confirm-text (get-in delete-ui [:confirm :text])}
                                        glyph)

                                      ;; Otherwise the record is about to stop
                                      ;; existing, and that earns a modal's friction.
                                      ;; It is the DEFAULT: an aggregate gets the
                                      ;; careful surface by saying nothing.
                                      :else
                                      ($ ui/table-action-icon
                                        {:color "red"
                                         :onClick (fn [e]
                                                    (.stopPropagation e)
                                                    (handle-delete-click (:id item)))}
                                        glyph))))))))))))))))]
      ($ ui/box {}
        ;; Header
        ($ ui/group {:justify "space-between" :mb "sm"}
          ($ ui/title {:order 4} (or title (str (name child-agg-key) "s")))
          ;; ADD SEMANTICS FOLLOW THE MODE (F-060).
          ;;
          ;;   :link    a junction — you PICK the far side, so "Link a …"
          ;;   :create  an owned child — a real create form
          ;;   :none    an independent record that merely points here, and
          ;;            there is no button at all
          ;;
          ;; `:none` is the one that earns its keep: a Lead names a Guardian,
          ;; but leads arrive from a conversation or a walk-in. `+ Add` on a
          ;; guardian's Leads tab is an affordance for something nobody does.
          (when (and (not create-disabled?) (not= :none (some-> add keyword)))
            ($ ui/button
              {:size "xs"
               :variant "light"
               :leftSection ($ icons/icon-plus {:size 16})
               :onClick handle-add}
              (if (= :link (some-> add keyword))
                (str "Link "
                     (if-let [far (some-> junction :far :aggregate name
                                          (str/replace "-" " "))]
                       (str "a " far)
                       "one"))
                "Add"))))
        (when-let [msg (and create-disabled? (not read-only?) (:message child-restrictions))]
          ($ ui/alert {:color "blue" :variant "light" :mb "sm"} msg))
        content

        ;; The default destroy surface. A record is about to stop existing, so it
        ;; gets a modal: focus trapped, no dismiss-by-click-away. A reversible
        ;; action opts out into the anchored popover above.
        ($ ui/modal {:opened (and (some? delete-confirm-id)
                                  (not= "popover" (get-in delete-ui [:confirm :widget])))
                     :onClose handle-delete-cancel
                     :title (get-in delete-ui [:confirm :title])
                     :centered true
                     :size "sm"}
          ($ ui/stack {:gap "md"}
            ($ ui/text {:size "sm"}
               (get-in delete-ui [:confirm :text]))
            ($ ui/group {:justify "flex-end" :gap "sm"}
              ($ ui/button {:variant "subtle" :color "gray" :size "sm"
                            :onClick handle-delete-cancel}
                 "Cancel")
              ($ ui/destructive-button {:size "sm" :loading deleting?
                                        :onClick handle-delete-confirm}
                 (get-in delete-ui [:confirm :label])))))

        ;; Slide-over for add/edit
        ($ slide-over/slide-over
          {:open? (some? slide-over-state)
           :on-close handle-close
           :title (let [singular (if (and title (str/ends-with? title "s"))
                                    (subs title 0 (dec (count title)))
                                    (or title "Item"))]
                    (case (:mode slide-over-state)
                      :add (str "Add " singular)
                      :edit (str "Edit " singular)
                      singular))
           :size "lg"}
          (when slide-over-state
            (if (and custom-add-component (= (:mode slide-over-state) :add))
              ($ custom-add-component
                {:parent-agg-key parent-agg-key :parent-id parent-id
                 :child-agg-key child-agg-key :fk-field fk-field
                 :on-close handle-close :parent-record parent-record
                 :inline-form-props (merge slide-over-form-props
                                           {:mode :add
                                            :item resolved-defaults})})
              ($ child-slide-over-form
                (merge slide-over-form-props
                       (if (= (:mode slide-over-state) :edit)
                         {:mode :edit :item current-item
                          ;; PATCH schema for edit (falls back to create until
                          ;; it loads) so protected fields drop out (#831).
                          :schema (or edit-schema schema)
                          :key (str "edit-" (:id slide-over-state))}
                         {:mode :add :item (or resolved-defaults {})
                          :key "add-form"}))))))))))
